/**
 * Slide-in task drawer. Right-side, 40% width on desktop, full on mobile.
 *
 * Layout:
 *   - Header: key, status, priority, close
 *   - Tabs: Details (default) | Comments | Activity
 *   - Details: title (inline editable), meta sidebar, description, pull
 *     requests and subtasks
 *   - Comments: the comment thread + the composer (FRIDAY-74)
 *   - Activity: the task's change history
 *
 * The drawer stays mounted while the user moves between tasks, so every piece
 * of view state (active tab, unsaved drafts) is scoped to the task id.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'framer-motion'
import {
  useArchiveTask,
  useBoard,
  useDeleteTaskHard,
  useTask,
  useUpdateTask,
  useComments,
  useActivity,
  useAddComment,
} from '@/features/projects/board'
import { useProfiles, pickDisplay, type PublicProfile } from '@/features/profiles/profiles'
import { useAuth } from '@/features/auth/authContext'
import { ConfirmDialog, KebabMenu, type KebabMenuItem } from '@/components/feedback'
import { toast } from '@/features/feedback/toast'
import { cn } from '@/lib/utils'
import { api, ApiError } from '@/lib/api'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Activity, ActivityAction, Task, WorkspaceMember } from '@friday/shared/realtime'
import { MentionTextarea, type MentionCandidate } from './MentionTextarea'
import { AssigneePicker } from './AssigneePicker'
import { Markdown } from './Markdown'
import {
  useAttachPageToTask,
  useDetachPageFromTask,
  usePageTree,
  useTaskPageRefs,
} from '@/features/notebook/pages'
import { PagePicker } from '@/components/notebook/PagePicker'
import { UserAvatar } from '@/components/user/UserAvatar'
import { Tabs, TabPanel } from '@/components/ui/Tabs'

interface DrawerProps {
  wsId: string
  pId: string
  taskId: string | null
  onClose: () => void
}

export function TaskDrawer({ wsId, pId, taskId, onClose }: DrawerProps) {
  const open = taskId !== null
  useEscape(onClose, open)

  return (
    <AnimatePresence>
      {open && taskId && (
        <>
          <motion.div
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="fixed inset-0 z-[55] bg-black/30"
          />
          <motion.aside
            key="panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            role="dialog"
            aria-modal="true"
            aria-label="Task details"
            className="task-detail-panel border-surface-200 dark:border-surface-800 dark:bg-surface-900 fixed right-0 top-0 z-[60] flex h-full w-full max-w-[640px] flex-col border-l bg-white shadow-2xl"
          >
            {taskId && <TaskDrawerBody wsId={wsId} pId={pId} taskId={taskId} onClose={onClose} />}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

function TaskDrawerBody({
  wsId,
  pId,
  taskId,
  onClose,
}: {
  wsId: string
  pId: string
  taskId: string
  onClose: () => void
}) {
  const board = useBoard(wsId, pId)
  const taskQ = useTask(wsId, pId, taskId)
  const view = useDrawerView(taskId)
  // The activity query stays disabled until the Activity tab is opened for
  // this task, then keeps sharing the same cache entry. The comments query
  // follows the same rule for the Comments tab (FRIDAY-74).
  const activityQ = useActivity(taskId, view.state.activityOpened)
  const commentsQ = useComments(taskId, view.state.commentsOpened)
  const update = useUpdateTask(wsId, pId)
  const addComment = useAddComment(taskId)
  const archiveTask = useArchiveTask(wsId, pId)
  const deleteTask = useDeleteTaskHard(wsId, pId)
  const [pendingArchive, setPendingArchive] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(false)

  const task = taskQ.data ?? board.data?.tasks.find((t) => t.id === taskId)
  const { user: currentUser } = useAuth()
  const { profiles } = useProfiles(
    [task?.assigneeUserId, task?.reporterUserId, currentUser?.id].filter(
      (id): id is string => typeof id === 'string' && id.length > 0,
    ),
  )
  if (!task) {
    return (
      <div className="text-surface-500 flex h-full items-center justify-center text-sm">
        {taskQ.isLoading ? 'Loading…' : 'Task not found'}
      </div>
    )
  }

  const column = board.data?.columns.find((c) => c.id === task.columnId)
  const sprint = board.data?.sprints.find((s) => s.id === task.sprintId)
  const milestones = board.data?.milestones ?? []
  const milestone = milestones.find((m) => m.id === task.milestoneId)
  const labels = task.labels ?? []
  const wsMembers: WorkspaceMember[] = board.data?.members ?? []
  // Candidate list for the @-mention dropdown and the assignee picker. The
  // board's `members` is the workspace roster; that's our universe of
  // people to choose from for either surface.
  const candidateIds = wsMembers.map((m) => m.userId)
  const mentionCandidates: MentionCandidate[] = candidateIds.map((id) => ({ id, label: id }))

  return (
    <>
      <header className="border-surface-200 dark:border-surface-800 flex items-center justify-between border-b px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-surface-500 font-mono text-xs">{task.key}</span>
          <StatusPill
            task={task}
            columns={board.data?.columns ?? []}
            onChange={(cId) =>
              update.mutate({ taskId: task.id, version: task.version, patch: { columnId: cId } })
            }
          />
        </div>
        <div className="flex items-center gap-1.5">
          <PrioritySelect
            value={task.priority}
            onChange={(p) =>
              update.mutate({ taskId: task.id, version: task.version, patch: { priority: p } })
            }
          />
          <KebabMenu
            items={
              [
                {
                  key: 'archive',
                  label: task.archivedAt ? 'Already archived' : 'Archive task…',
                  disabled: !!task.archivedAt,
                  destructive: true,
                  onSelect: () => setPendingArchive(true),
                },
                {
                  key: 'delete',
                  label: 'Delete permanently…',
                  disabled: !task.archivedAt,
                  destructive: true,
                  onSelect: () => setPendingDelete(true),
                },
              ] satisfies KebabMenuItem[]
            }
            ariaLabel="Task actions"
          />
          <button onClick={onClose} className="btn-ghost px-2 py-1 text-lg" aria-label="Close">
            ×
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col">
        <Tabs
          label="Task sections"
          idBase="task-tab"
          activeId={view.state.tab}
          onSelect={(id) => {
            if (id === 'details' || id === 'comments' || id === 'activity') view.setTab(id)
          }}
          tabs={[
            { id: 'details', label: 'Details' },
            { id: 'comments', label: 'Comments' },
            { id: 'activity', label: 'Activity' },
          ]}
        />

        {view.state.tab === 'details' ? (
          <TabPanel id="details" idBase="task-tab" className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="space-y-5">
              <TitleEditor
                task={task}
                onSave={(title) =>
                  update.mutate({ taskId: task.id, version: task.version, patch: { title } })
                }
              />

              <MetaGrid
                task={task}
                wsId={wsId}
                sprint={sprint}
                milestone={milestone}
                milestones={milestones}
                column={column}
                labels={labels as Array<{ id: string; name: string; color: string }>}
                profiles={profiles}
                currentUser={currentUser}
                assignees={candidateIds.map((id) => ({ id }))}
                onPatch={(patch) =>
                  update.mutate({ taskId: task.id, version: task.version, patch })
                }
              />

              <DescriptionSection
                task={task}
                draft={view.state.description}
                candidates={mentionCandidates}
                onDraftChange={view.setDescriptionDraft}
                onSave={(descriptionMd) =>
                  update.mutate({ taskId: task.id, version: task.version, patch: { descriptionMd } })
                }
              />

              <GithubLinks task={task} />
              <SubtaskSection task={task} wsId={wsId} />
            </div>
          </TabPanel>
        ) : view.state.tab === 'comments' ? (
          <TabPanel
            id="comments"
            idBase="task-tab"
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <CommentSection
              comments={commentsQ.data ?? []}
              isLoading={commentsQ.isLoading}
              isError={commentsQ.isError}
              onRetry={() => {
                void commentsQ.refetch()
              }}
              draft={view.state.comment}
              candidates={mentionCandidates}
              onDraftChange={view.setCommentDraft}
              onSubmit={async (body, mentions) => {
                await addComment.mutateAsync({ bodyMd: body, mentions })
              }}
              isPosting={addComment.isPending}
            />
          </TabPanel>
        ) : (
          <TabPanel id="activity" idBase="task-tab" className="min-h-0 flex-1 overflow-y-auto p-4">
            <ActivitySection
              activity={activityQ.data ?? []}
              columns={board.data?.columns ?? []}
              isLoading={activityQ.isLoading}
              isError={activityQ.isError}
              onRetry={() => {
                void activityQ.refetch()
              }}
            />
          </TabPanel>
        )}
      </div>

      <ConfirmDialog
        open={pendingArchive}
        title="Archive this task?"
        body={
          <div className="text-surface-600 dark:text-surface-300 space-y-2 text-sm">
            <p>The task will be hidden from the board. You can restore it later from the archive.</p>
            <p className="text-surface-500 text-xs">
              To remove it permanently, open the task again from the archive and choose Delete permanently.
            </p>
          </div>
        }
        confirmLabel="Archive task"
        destructive
        busy={archiveTask.isPending}
        onClose={() => !archiveTask.isPending && setPendingArchive(false)}
        onConfirm={async () => {
          try {
            await archiveTask.mutateAsync(task.id)
            setPendingArchive(false)
            toast(`Task ${task.key} archived`, { kind: 'success' })
            onClose()
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Failed to archive task'
            toast(msg, { kind: 'error' })
          }
        }}
      />
      <ConfirmDialog
        open={pendingDelete}
        title="Delete this task permanently?"
        body={
          <div className="text-surface-600 dark:text-surface-300 space-y-2 text-sm">
            <p>
              The task and all of its comments, subtasks, and activity will be removed. This cannot be undone.
            </p>
          </div>
        }
        confirmLabel="Delete permanently"
        destructive
        busy={deleteTask.isPending}
        onClose={() => !deleteTask.isPending && setPendingDelete(false)}
        onConfirm={async () => {
          try {
            await deleteTask.mutateAsync(task.id)
            setPendingDelete(false)
            toast(`Task ${task.key} deleted`, { kind: 'success' })
            onClose()
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Failed to delete task'
            toast(msg, { kind: 'error' })
          }
        }}
      />
    </>
  )
}

function useEscape(cb: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cb()
    }
    window.addEventListener('keydown', fn)
    return () => window.removeEventListener('keydown', fn)
  }, [cb, enabled])
}

type DrawerTab = 'details' | 'comments' | 'activity'

interface DescriptionDraft {
  /** Unsent description text. */
  value: string
  /** True while the description textarea is open. */
  editing: boolean
  mentions: string[]
}

interface CommentDraft {
  value: string
  mentions: string[]
}

interface DrawerViewState {
  taskId: string
  tab: DrawerTab
  /** True once the Activity tab has been opened for this task. */
  activityOpened: boolean
  /** True once the Comments tab has been opened for this task. */
  commentsOpened: boolean
  description: DescriptionDraft
  comment: CommentDraft
}

function initialDrawerView(taskId: string): DrawerViewState {
  return {
    taskId,
    tab: 'details',
    activityOpened: false,
    commentsOpened: false,
    description: { value: '', editing: false, mentions: [] },
    comment: { value: '', mentions: [] },
  }
}

/**
 * View state that belongs to one task: the active tab, whether the Activity
 * tab has been opened for it, and the unsaved description / comment drafts.
 *
 * The drawer is not remounted when the task changes, so the state is keyed by
 * task id and reset during render rather than in an effect — that way the
 * drawer never paints a frame of the previous task's tab or draft.
 */
function useDrawerView(taskId: string) {
  const [stored, setStored] = useState<DrawerViewState>(() => initialDrawerView(taskId))
  const state = stored.taskId === taskId ? stored : initialDrawerView(taskId)
  if (state !== stored) setStored(state)

  const patch = useCallback(
    (update: (prev: DrawerViewState) => DrawerViewState) => {
      setStored((prev) => {
        const base = prev.taskId === taskId ? prev : initialDrawerView(taskId)
        return { ...update(base), taskId }
      })
    },
    [taskId],
  )

  const setTab = useCallback(
    (tab: DrawerTab) =>
      patch((prev) => ({
        ...prev,
        tab,
        activityOpened: prev.activityOpened || tab === 'activity',
        commentsOpened: prev.commentsOpened || tab === 'comments',
      })),
    [patch],
  )
  const setDescriptionDraft = useCallback(
    (update: (prev: DescriptionDraft) => DescriptionDraft) =>
      patch((prev) => ({ ...prev, description: update(prev.description) })),
    [patch],
  )
  const setCommentDraft = useCallback(
    (update: (prev: CommentDraft) => CommentDraft) =>
      patch((prev) => ({ ...prev, comment: update(prev.comment) })),
    [patch],
  )

  return { state, setTab, setDescriptionDraft, setCommentDraft }
}

/** Exported for the browser test harness only. */
export function StatusPill({
  task,
  columns,
  onChange,
}: {
  task: Task
  columns: Array<{ id: string; name: string; color: string }>
  onChange: (columnId: string) => void
}) {
  const col = columns.find((c) => c.id === task.columnId)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const close = (restoreFocus = false) => {
    setOpen(false)
    if (restoreFocus) requestAnimationFrame(() => triggerRef.current?.focus())
  }

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !rootRef.current?.contains(e.target)) close(true)
    }
    // Registered on `document` so it runs before the drawer's window-level
    // Escape handler; stopping propagation keeps the drawer open.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        close(true)
      }
    }
    const items = rootRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]')
    const current = Array.from(items ?? []).find((el) => el.getAttribute('aria-checked') === 'true')
    ;(current ?? items?.[0])?.focus()
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Status: ${col?.name ?? 'Unknown'}`}
        className="hover:bg-surface-100 dark:hover:bg-surface-800 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
        style={{ backgroundColor: (col?.color ?? '#999') + '20', color: col?.color }}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: col?.color }} />
        {col?.name ?? 'Unknown'}
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Change status"
          className="border-surface-200 dark:border-surface-700 dark:bg-surface-800 absolute z-50 mt-1 w-44 rounded-md border bg-white py-1 shadow-lg"
        >
          {columns.map((c) => (
            <button
              key={c.id}
              type="button"
              role="menuitemradio"
              aria-checked={c.id === task.columnId}
              onClick={() => {
                onChange(c.id)
                close(true)
              }}
              className={cn(
                'hover:bg-surface-100 dark:hover:bg-surface-800 flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
                c.id === task.columnId && 'font-semibold',
              )}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function PrioritySelect({
  value,
  onChange,
}: {
  value: Task['priority']
  onChange: (p: Task['priority']) => void
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as Task['priority'])}
      className="border-surface-300 dark:border-surface-700 rounded border bg-transparent px-2 py-1 text-xs"
    >
      <option value="urgent">Urgent</option>
      <option value="high">High</option>
      <option value="medium">Medium</option>
      <option value="low">Low</option>
    </select>
  )
}

function TitleEditor({ task, onSave }: { task: Task; onSave: (title: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(task.title)
  useEffect(() => setVal(task.title), [task.title])
  if (!editing) {
    return (
      <h1
        className="cursor-text text-xl font-semibold"
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {task.title}
      </h1>
    )
  }
  return (
    <input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        const t = val.trim()
        if (t && t !== task.title) onSave(t)
        setEditing(false)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') {
          setVal(task.title)
          setEditing(false)
        }
      }}
      className="input text-lg font-semibold"
    />
  )
}

function MetaGrid({
  task,
  wsId,
  sprint,
  milestone,
  milestones,
  column,
  labels,
  profiles,
  currentUser,
  assignees,
  onPatch,
}: {
  task: Task
  wsId: string
  sprint?: { id: string; name: string }
  milestone?: { id: string; name: string; color: string }
  milestones?: Array<{ id: string; name: string; color: string }>
  column?: { id: string; name: string; color: string }
  labels: Array<{ id: string; name: string; color: string }>
  profiles: Record<string, PublicProfile>
  currentUser?: {
    id: string
    displayName?: string
    email?: string
    avatarUrl?: string | null
    alias?: string | null
    customAvatarUrl?: string | null
  } | null
  assignees: Array<{ id: string }>
  onPatch: (patch: Record<string, unknown>) => void
}) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
      <Meta label="Assignee">
        <AssigneePicker
          value={task.assigneeUserId ?? null}
          candidates={assignees}
          onChange={(id) => onPatch({ assigneeUserId: id })}
          currentUserId={currentUser?.id ?? null}
        />
      </Meta>
      <Meta label="Created by">
        <PersonChip userId={task.reporterUserId ?? null} profiles={profiles} currentUser={currentUser} />
      </Meta>
      <Meta label="Due date">
        <DateField value={task.dueDate ?? null} onChange={(v) => onPatch({ dueDate: v })} />
      </Meta>
      <Meta label="Story points">
        <NumberField
          value={task.storyPoints ?? null}
          onChange={(v) => onPatch({ storyPoints: v })}
          suffix="pts"
        />
      </Meta>
      <Meta label="Sprint">
        <span className="text-surface-700 dark:text-surface-300">{sprint?.name ?? 'Backlog'}</span>
      </Meta>
      <Meta label="Milestone">
        <select
          className="input py-0.5 text-xs"
          value={task.milestoneId ?? ''}
          onChange={(e) => onPatch({ milestoneId: e.target.value || null })}
        >
          <option value="">No milestone</option>
          {(milestones ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        {milestone && (
          <div className="text-surface-500 mt-1 flex items-center gap-1 text-[10px]">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: milestone.color }}
            />
            {milestone.name}
          </div>
        )}
      </Meta>
      <Meta label="Status">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: column?.color }} />
          {column?.name ?? '—'}
        </span>
      </Meta>
      <Meta label="Labels">
        {labels.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {labels.map((l) => (
              <span
                key={l.id}
                className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{ backgroundColor: l.color + '25', color: l.color }}
              >
                {l.name}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-surface-500">None</span>
        )}
      </Meta>
      <Meta label="Pages">
        <PageRefs wsId={wsId} taskId={task.id} />
      </Meta>
    </dl>
  )
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-surface-500 mb-0.5">{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/**
 * PageRefs — the "Referenced in" row in the task drawer.
 *
 * Renders each ref as a pill with the page's icon + title. Clicking the ×
 * on a pill detaches the reference. The "Add page" picker is the standard
 * PagePicker (cmdk palette comes in Phase 4).
 */
function PageRefs({ wsId, taskId }: { wsId: string; taskId: string }) {
  const refsQ = useTaskPageRefs(taskId)
  const attach = useAttachPageToTask(wsId)
  const detach = useDetachPageFromTask(wsId)
  // We need page metadata for the pill labels. Easiest: keep a single
  // usePageTree call and look up by id. For very large workspaces this is
  // wasteful — Phase 5 switches to a dedicated /tasks/:tId/pages endpoint
  // that returns the full Page object.
  const tree = usePageTree(wsId)
  const pageById = new Map((tree.data ?? []).map((p) => [p.id, p]))

  if (refsQ.isLoading) return <span className="text-surface-500">Loading…</span>
  const refs = refsQ.data ?? []

  return (
    <div className="flex flex-wrap items-center gap-1">
      {refs.length === 0 && <span className="text-surface-500">None</span>}
      {refs.map((r) => {
        const p = pageById.get(r.pageId)
        return (
          <span
            key={r.pageId}
            className="border-surface-200 text-surface-700 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-200 inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[10px]"
          >
            <span>{p?.icon ?? '📄'}</span>
            <span className="max-w-[10rem] truncate">{p?.title ?? r.pageId.slice(0, 6)}</span>
            <button
              type="button"
              onClick={() => detach.mutate({ taskId, pageId: r.pageId })}
              className="text-surface-400 hover:text-red-500"
              aria-label="Remove reference"
            >
              ×
            </button>
          </span>
        )
      })}
      <PagePicker
        wsId={wsId}
        excludeIds={refs.map((r) => r.pageId)}
        onPick={(pageId) => attach.mutate({ taskId, pageId })}
        placeholder="Add page"
      />
    </div>
  )
}

function DateField({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  return (
    <input
      type="date"
      value={value ? value.slice(0, 10) : ''}
      onChange={(e) => onChange(e.target.value ? e.target.value : null)}
      className="input py-0.5 text-xs"
    />
  )
}

function NumberField({
  value,
  onChange,
  suffix,
}: {
  value: number | null
  onChange: (v: number | null) => void
  suffix?: string
}) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={0}
        max={99}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="input w-16 py-0.5 text-xs"
      />
      {suffix && <span className="text-surface-500">{suffix}</span>}
    </div>
  )
}

function DescriptionSection({
  task,
  draft,
  candidates,
  onDraftChange,
  onSave,
}: {
  task: Task
  draft: DescriptionDraft
  candidates: MentionCandidate[]
  onDraftChange: (update: (prev: DescriptionDraft) => DescriptionDraft) => void
  onSave: (md: string) => void
}) {
  const reset = () => onDraftChange(() => ({ value: '', editing: false, mentions: [] }))
  return (
    <section>
      <h3 className="text-surface-500 mb-1 text-xs font-semibold uppercase tracking-wider">Description</h3>
      {draft.editing ? (
        <div className="space-y-2">
          <MentionTextarea
            value={draft.value}
            onChange={(value) => onDraftChange((d) => ({ ...d, value }))}
            onMentionsChange={(mentions) => onDraftChange((d) => ({ ...d, mentions }))}
            candidates={candidates}
            placeholder="Add a description… markdown + @mentions supported."
            className="min-h-[6rem]"
            rows={5}
          />
          <div className="flex items-center justify-between">
            <span className="text-surface-500 text-[10px]">
              {draft.mentions.length > 0
                ? `${draft.mentions.length} mention${draft.mentions.length === 1 ? '' : 's'}`
                : 'Type @ to mention someone'}
            </span>
            <div className="flex gap-1.5">
              <button onClick={reset} className="btn-ghost text-xs">
                Cancel
              </button>
              <button
                onClick={() => {
                  onSave(draft.value)
                  reset()
                }}
                className="btn-primary text-xs"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onClick={() =>
            onDraftChange((d) => ({ ...d, value: task.descriptionMd ?? '', editing: true, mentions: [] }))
          }
          className="border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 hover:border-brand-500 min-h-[3rem] cursor-text rounded-md border border-dashed p-2 text-sm"
        >
          {task.descriptionMd ? (
            <Markdown text={task.descriptionMd} />
          ) : (
            <span className="text-surface-500">Add a description…</span>
          )}
        </div>
      )}
    </section>
  )
}

type GithubRelease = {
  environment: string
  status: 'observing' | 'passed' | 'failed'
  reason: string | null
  deployedAt: string
  observeUntil: string | null
}
type GithubLink = {
  repository: string
  number: number
  url: string
  state: string
  baseBranch: string
  release?: GithubRelease | null
}

/** FRIDAY-24: why a linked PR has or has not completed its task. */
export function releaseSummary(link: Pick<GithubLink, 'state' | 'release'>): { text: string; tone: 'ok' | 'wait' | 'bad' } | null {
  if (link.state !== 'merged') return null
  const r = link.release
  const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (!r) return { text: 'Merged · waiting for a production deployment', tone: 'wait' }
  if (r.status === 'passed') return { text: `Deployed to ${r.environment} ${time(r.deployedAt)} · checks passed`, tone: 'ok' }
  if (r.status === 'observing')
    return {
      text: `Deployed to ${r.environment} ${time(r.deployedAt)} · observing until ${r.observeUntil ? time(r.observeUntil) : 'soon'}`,
      tone: 'wait',
    }
  return { text: `Release blocked: ${r.reason ?? 'checks failed'}`, tone: 'bad' }
}
/** Exported for the browser test harness. */
export function GithubLinks({ task }: { task: Task }) {
  const links = useQuery({
    queryKey: ['task-github', task.id, task.version],
    queryFn: () => api<GithubLink[]>(`/api/v1/tasks/${task.id}/github`),
    retry: false,
  })
  if (links.isError) return <p className="text-sm text-surface-500">Pull requests are temporarily unavailable.</p>
  if (!links.data?.length) return null
  return (
    <section aria-label="Pull requests">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-surface-500">Pull requests</h3>
      <ul className="space-y-1">
        {links.data.map(link => {
          const summary = releaseSummary(link)
          return (
            <li key={`${link.repository}/${link.number}`}>
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="flex min-h-11 flex-wrap items-center gap-2 rounded px-2 text-sm text-primary-600 underline">
                <span>{link.repository} #{link.number}</span>
                <span className="text-surface-500">{link.state} · {link.baseBranch}</span>
              </a>
              {summary && (
                <p
                  data-release-tone={summary.tone}
                  className={cn('px-2 text-xs', summary.tone === 'bad' ? 'text-red-600' : summary.tone === 'ok' ? 'text-green-700 dark:text-green-400' : 'text-surface-500')}
                >
                  {summary.text}
                </p>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function SubtaskSection({ task, wsId }: { task: Task; wsId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['subtasks', task.id],
    queryFn: () => api<Task[]>(`/api/v1/tasks/${task.id}/subtasks`),
  })
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')
  const list = data ?? []

  const [pending, setPending] = useState(false)
  function refresh() {
    void qc.invalidateQueries({ queryKey: ['subtasks', task.id] })
    void qc.invalidateQueries({ queryKey: ['task', task.id] })
    void qc.invalidateQueries({ queryKey: ['board', wsId, task.projectId] })
    void qc.invalidateQueries({ queryKey: ['my-tasks'] })
  }
  async function toggle(s: Task) {
    if (pending) return
    setPending(true)
    try {
      await api(`/api/v1/tasks/${s.id}?version=${s.version}`, { method: 'PATCH', body: { status: s.status === 'done' ? 'open' : 'done' } })
    } catch (error) { toast(error instanceof Error ? error.message : 'Could not update subtask', { kind: 'error' }) }
    finally { refresh(); setPending(false) }
  }
  async function add(e: React.FormEvent) {
    e.preventDefault()
    if (!draft.trim() || pending) return
    setPending(true)
    try {
      await api(`/api/v1/tasks/${task.id}/subtasks`, { method: 'POST', body: { title: draft.trim() } })
      setDraft('')
    } catch (error) { toast(error instanceof Error ? error.message : 'Could not add subtask', { kind: 'error' }) }
    finally { refresh(); setPending(false) }
  }

  return (
    <section>
      <h3 className="text-surface-500 mb-1 text-xs font-semibold uppercase tracking-wider">
        Subtasks{' '}
        {list.length > 0 && (
          <span className="text-surface-400">
            ({list.filter((s) => s.status === 'done').length}/{list.length})
          </span>
        )}
      </h3>
      {isLoading ? (
        <div className="text-surface-500 text-xs">Loading…</div>
      ) : (
        <ul className="space-y-1">
          {list.map((s) => (
            <li key={s.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={s.status === 'done'}
                disabled={pending}
                aria-label={`Complete ${s.key}`}
                onChange={() => toggle(s)}
                className="h-4 w-4 rounded"
              />
              <span className={cn(s.status === 'done' && 'text-surface-500 line-through')}>{s.title}</span>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="mt-2 flex gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="+ Add subtask"
          className="input text-xs"
        />
        <button type="submit" className="btn-ghost text-xs" disabled={!draft.trim() || pending}>
          Add
        </button>
      </form>
    </section>
  )
}

/**
 * Comment events are recorded by the server (comment.added / .updated /
 * .deleted) but the comment body belongs to the Details tab. The activity
 * feed therefore shows the event only — never the comment text — so the same
 * comment is never rendered twice. Nothing is removed server-side.
 */
const COMMENT_ACTIONS: ActivityAction[] = ['comment.added', 'comment.updated', 'comment.deleted']

const ACTIVITY_LABELS: Partial<Record<ActivityAction, string>> = {
  'task.created': 'Task created',
  'task.updated': 'Task updated',
  'task.moved': 'Moved to another column',
  'task.assigned': 'Assignee changed',
  'task.archived': 'Task archived',
  'task.restored': 'Task restored',
  'task.deleted': 'Task deleted',
  'task.page_attached': 'Page linked',
  'comment.added': 'Comment added',
  'comment.updated': 'Comment updated',
  'comment.deleted': 'Comment deleted',
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  descriptionMd: 'Description',
  priority: 'Priority',
  columnId: 'Status',
  assigneeUserId: 'Assignee',
  dueDate: 'Due date',
  startDate: 'Start date',
  storyPoints: 'Story points',
  sprintId: 'Sprint',
  milestoneId: 'Milestone',
  labelNames: 'Labels',
  archived: 'Archive state',
  parentTaskId: 'Parent task',
}

function activityLabel(action: ActivityAction): string {
  return ACTIVITY_LABELS[action] ?? action
}

/** Human summary of what an event changed, when the payload says so. */
function activityDetail(
  action: ActivityAction,
  payload: Record<string, unknown>,
  columns: Array<{ id: string; name: string }>,
): string | null {
  const columnName = (id: unknown) =>
    typeof id === 'string' ? columns.find((c) => c.id === id)?.name ?? id : null
  if (action === 'task.updated') {
    const fields = Array.isArray(payload.fields)
      ? payload.fields.filter((f): f is string => typeof f === 'string')
      : []
    if (fields.length === 0) return null
    return fields.map((f) => FIELD_LABELS[f] ?? f).join(', ')
  }
  if (action === 'task.moved') {
    const from = columnName(payload.from)
    const to = columnName(payload.to)
    if (from && to) return `${from} → ${to}`
    return to
  }
  return null
}

function ActivitySection({
  activity,
  columns,
  isLoading,
  isError,
  onRetry,
}: {
  activity: Activity[]
  columns: Array<{ id: string; name: string }>
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  return (
    <section aria-label="Activity">
      <h3 className="text-surface-500 mb-1 text-xs font-semibold uppercase tracking-wider">Activity</h3>
      {isLoading ? (
        <div className="text-surface-500 text-xs">Loading activity…</div>
      ) : isError ? (
        <div className="space-y-2">
          <p className="text-sm text-red-600 dark:text-red-400">Activity could not be loaded.</p>
          <button type="button" onClick={onRetry} className="btn-outline text-xs">
            Retry
          </button>
        </div>
      ) : activity.length === 0 ? (
        <div className="text-surface-500 text-xs">No activity yet.</div>
      ) : (
        <ul className="text-surface-600 dark:text-surface-300 space-y-1.5 text-xs">
          {activity.map((a) => {
            const detail = activityDetail(a.action, a.payload ?? {}, columns)
            const isComment = COMMENT_ACTIONS.includes(a.action)
            return (
              <li key={a.id} className="flex items-start gap-2">
                <span
                  className={cn(
                    'mt-1 h-1.5 w-1.5 rounded-full',
                    isComment ? 'bg-surface-300 dark:bg-surface-600' : 'bg-surface-400',
                  )}
                />
                <div>
                  <span
                    className={cn(
                      'font-medium',
                      isComment
                        ? 'text-surface-600 dark:text-surface-400'
                        : 'text-surface-800 dark:text-surface-100',
                    )}
                  >
                    {activityLabel(a.action)}
                  </span>{' '}
                  {detail && <span className="text-surface-600 dark:text-surface-300">{detail}</span>}{' '}
                  <span className="text-surface-500">{new Date(a.createdAt).toLocaleString()}</span>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

/**
 * The Comments tab (FRIDAY-74).
 *
 * The thread scrolls; the composer stays pinned under it so a draft is never
 * pushed off-screen while reading. The comment bodies are rendered by the
 * shared `Markdown` component, which keeps every block (task-list items,
 * code, tables) inside the comment bubble.
 */
function CommentSection({
  comments,
  isLoading,
  isError,
  onRetry,
  draft,
  candidates,
  onDraftChange,
  onSubmit,
  isPosting,
}: {
  comments: Array<{ id: string; bodyMd: string; createdAt: string }>
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  draft: CommentDraft
  candidates: MentionCandidate[]
  onDraftChange: (update: (prev: CommentDraft) => CommentDraft) => void
  onSubmit: (body: string, mentions: string[]) => Promise<void>
  isPosting: boolean
}) {
  const submitRef = useRef<(() => Promise<void> | void) | null>(null)
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <h3 className="text-surface-500 text-xs font-semibold uppercase tracking-wider">
          Comments {comments.length > 0 && <span className="text-surface-400">({comments.length})</span>}
        </h3>
        {isLoading ? (
          <p className="text-surface-500 text-xs">Loading comments…</p>
        ) : isError ? (
          <div className="space-y-2">
            <p className="text-sm text-red-600 dark:text-red-400">Comments could not be loaded.</p>
            <button type="button" onClick={onRetry} className="btn-outline text-xs">
              Retry
            </button>
          </div>
        ) : comments.length === 0 ? (
          <p className="text-surface-500 text-xs">No comments yet. Start the thread below.</p>
        ) : (
          comments.map((c) => (
            <div
              key={c.id}
              data-testid="comment-body"
              className="bg-surface-100 dark:bg-surface-800 overflow-hidden rounded-md p-2 text-sm"
            >
              <Markdown text={c.bodyMd} />
              <div className="text-surface-500 mt-1 text-[10px]">
                {new Date(c.createdAt).toLocaleString()}
              </div>
            </div>
          ))
        )}
      </div>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (!draft.value.trim()) return
          await onSubmit(draft.value.trim(), draft.mentions)
          onDraftChange(() => ({ value: '', mentions: [] }))
        }}
        className="border-surface-200 dark:border-surface-800 shrink-0 space-y-1.5 border-t p-4"
      >
        <MentionTextarea
          value={draft.value}
          onChange={(value) => onDraftChange((d) => ({ ...d, value }))}
          onMentionsChange={(mentions) => onDraftChange((d) => ({ ...d, mentions }))}
          candidates={candidates}
          placeholder="Write a comment… markdown + @mentions"
          submitRef={submitRef}
        />
        <div className="flex items-center justify-end gap-1.5">
          <span className="text-surface-500 text-[10px]">⌘/Ctrl+Enter to send</span>
          <button type="submit" disabled={!draft.value.trim() || isPosting} className="btn-primary text-xs">
            {isPosting ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </form>
    </div>
  )
}

void useParams
void useNavigate

/**
 * PersonChip — avatar + alias-or-name + email fallback. Used wherever we
 * show a user (assignee, comment author, etc).
 */
function PersonChip({
  userId,
  profiles,
  currentUser,
  size = 'sm',
}: {
  userId?: string | null
  profiles: Record<string, PublicProfile>
  currentUser?: {
    id: string
    displayName?: string
    email?: string
    avatarUrl?: string | null
    alias?: string | null
    customAvatarUrl?: string | null
  } | null
  size?: 'xs' | 'sm'
}) {
  const sizeCls = size === 'xs' ? 'h-5 w-5 text-[9px]' : 'h-6 w-6 text-[10px]'
  if (!userId) {
    return (
      <span className="text-surface-500 inline-flex items-center gap-2">
        <span
          className={cn(
            'bg-surface-200 dark:bg-surface-700 flex items-center justify-center rounded-full',
            sizeCls,
          )}
        >
          —
        </span>
        <span>Unassigned</span>
      </span>
    )
  }
  const profile = profiles[userId]
  const fallback =
    currentUser && currentUser.id === userId
      ? { displayName: currentUser.displayName, email: currentUser.email, avatarUrl: currentUser.avatarUrl }
      : undefined
  const display = pickDisplay(userId, profile, fallback)
  const sources =
    currentUser && currentUser.id === userId ? [currentUser.customAvatarUrl, ...display.avatarSources] : display.avatarSources
  return (
    <span className="inline-flex items-center gap-2">
      <UserAvatar name={display.displayName} email={display.email} sources={sources} className={sizeCls} pixelSize={48} decorative />
      <span className="text-surface-800 dark:text-surface-100">{display.displayName}</span>
    </span>
  )
}
